import React, { Suspense, lazy, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { BrowserRouter, Redirect, withRouter, Switch, Route, useHistory, useLocation } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import "../../css/app.css"
import "../../css/tables.css"
import "../../css/mydevicon-v1.0/style.css"
import PrivateRoute from "../helpers/PrivateRoute";
import { Provider, useDispatch, useSelector } from "react-redux";
import { store } from "./Store";
import { fetchUser, fetchStatuses } from "../redux/actions";
import { WORKSPACE_LOCKED_ALLOWED_PATHS } from "../helpers/Requests";
import RouteFallback from "./RouteFallback";
import ChunkErrorBoundary from "./ChunkErrorBoundary";
import { dismissDashboardOpening } from "../helpers/dashboardTransition";

// Light / auth shells — keep eager so login redirects are instant
import Login from "./Pages/Login";
import SignUp from "./Pages/SignUp";
import ConfirmPassword from "./Pages/ConfirmPassword";
import ResetPassword from "./Pages/ResetPassword";
import ForgotPassword from "./Pages/ForgotPassword";
import NotFound from "./Pages/NotFound";
import ServerError from "./Pages/ServerError";

// Core sidebar destinations — eager so tab switches never white-screen
import Dashboard from "./Pages/Dashboard/Dashboard";
import Tickets from './Pages/tickets/Tickets';
import TicketDetails from "./Pages/tickets/openTickets/TicketDetails";
import Users from "./Pages/Users";
import Departments from "./Pages/Departments";
import Emails from "./Pages/Emails";
import Profile from "./Pages/Profile/Profile";
// Embed ticket forms — eager (was chunk-splitting to a file the deploy wasn't serving)
import EmbedTicketForms from "./Pages/embedForms/EmbedTicketForms";
import EmbedTicketFormEditor from "./Pages/embedForms/EmbedTicketFormEditor";
import EmbedTrustedDomains from "./Pages/embedForms/EmbedTrustedDomains";
// Settings / Subscription / Reports / Developer — eager, same reason
import Tags from "./Pages/settings/Tickets/Tags";
import QuickReplies from "./Pages/settings/Tickets/QuickReplies";
import Types from "./Pages/settings/Tickets/Types";
import Priority from "./Pages/settings/Tickets/Priority";
import TransferConnections from "./Pages/settings/Tickets/TransferConnections";
import Connection from "./Pages/settings/Connection";
import Plan from "./Pages/subscription/Plan";
import Payment from "./Pages/subscription/Payment";
import Invoice from "./Pages/subscription/Invoice";
import Overview from "./Pages/reports/Overview/Overview";
import Activity from "./Pages/reports/Activity";
import Team from "./Pages/reports/Team/Team";
import Time from "./Pages/reports/Time/Time";
import TicketsReports from "./Pages/reports/Tickets/Tickets";
import DeveloperOption from "./Pages/DeveloperOptions";
// Keep authenticated app routes eager so deploys never depend on extra route chunks.
import SwitchWorkspace from "../modals/Workspaces/SwitchWorkspace";
import Claritask from "./Pages/Claritask";
import ClaritaskEmail from "./Pages/ClaritaskEmail";
import VerifyEmail from "./Pages/VerifyEmail";
import PublicTicketTrack from "./Pages/PublicTicketTrack";
import NotificationPreferences from "./Pages/NotificationPreferences";
import LockedWorkspace from "./Pages/workspace/LockedWorkspace";

const HomePage = lazy(() => import(/* webpackChunkName: "page-home" */ "../../main/HomePage"));
const Blog = lazy(() => import(/* webpackChunkName: "page-blog" */ "../../main/Blog/Blog"));
const BlogDetails = lazy(() => import(/* webpackChunkName: "page-blog-detail" */ "../../main/Blog/Blog_details"));

function App() {
	const dispatch = useDispatch()
	const history = useHistory();
	const location = useLocation();
	const loggedIn = useSelector(state => state.loggedReducer);
	useEffect(() => {
		if (loggedIn) {
			dispatch(fetchUser())
			dispatch(fetchStatuses())
		}
	}, [loggedIn])

	useEffect(() => {
		if (!loggedIn && (location.pathname === "/dashboard" || location.pathname.startsWith("/dashboard/"))) {
			dismissDashboardOpening();
		}
	}, [loggedIn, location.pathname]);
	const user = useSelector(state => state?.userReducer?.user);

	useEffect(() => {
		if (!loggedIn || !user?.workspace_locked) {
			return;
		}
		if (WORKSPACE_LOCKED_ALLOWED_PATHS.some((path) => location.pathname.startsWith(path))) {
			return;
		}
		history.replace('/workspace/locked');
	}, [loggedIn, user?.workspace_locked, location.pathname, history]);

	return (
		<>
			<div className="App">
				<ChunkErrorBoundary>
					<Suspense fallback={<RouteFallback />}>
					<Switch>
						<Route exact path="/" component={HomePage} />
						<Route exact path="/login" component={Login} />
						<Route exact path="/signup" component={SignUp} />
						<Route exact path="/register/confirm/:id" component={ConfirmPassword} />
						<Route exact path="/password/reset" component={ResetPassword} />
						<Route exact path="/forgot" component={ForgotPassword} />
						{/* Marketing pages (/how-it-works, /claritask, /pricing, /contact) live in LandingApp */}
						<Route exact path="/blog" component={Blog} />
						<Route exact path="/blog/:slug" component={BlogDetails} />
						<Route exact path="/claritask/:id" component={Claritask} />
						<Route exact path="/claritask-email" component={ClaritaskEmail} />
						<Route exact path="/track/:token" component={PublicTicketTrack} />

						<PrivateRoute path="/verify-email" authed={loggedIn} component={VerifyEmail} />

						<PrivateRoute path="/switch-workspace" authed={loggedIn} component={SwitchWorkspace} />
						<PrivateRoute path="/workspace/locked" authed={loggedIn} component={LockedWorkspace} />

						<PrivateRoute path="/dashboard" authed={loggedIn} component={Dashboard} userRole={user?.isAgent || user?.isCustomer} />
						<PrivateRoute path="/profile" authed={loggedIn} component={Profile} userRole={user?.isAgent || user?.isCustomer} />

						<PrivateRoute path="/notification-preferences" authed={loggedIn} component={NotificationPreferences} userRole={user?.isAgent || user?.isCustomer || user?.isAdmin} />

						<PrivateRoute path="/reports/overview" authed={loggedIn} component={Overview} userRole={user?.isAgent} />
						<PrivateRoute path="/reports/activity" authed={loggedIn} component={Activity} userRole={user?.isAgent} />
						<PrivateRoute path="/reports/tickets" authed={loggedIn} component={TicketsReports} userRole={user?.isAgent} />
						<PrivateRoute path="/reports/team" authed={loggedIn} component={Team} userRole={user?.isAgent} />
						<PrivateRoute path="/reports/time" authed={loggedIn} component={Time} userRole={user?.isAgent} />

						<PrivateRoute exact path="/tickets" authed={loggedIn} component={Tickets} userRole={user?.isAgent || user?.isCustomer} />
						<PrivateRoute exact path="/tickets/:category" authed={loggedIn} component={Tickets} userRole={user?.isAgent || user?.isCustomer} />
						<PrivateRoute path="/ticket/:id(\d+)" authed={loggedIn} component={TicketDetails} userRole={user?.isAgent || user?.isCustomer} />

						<PrivateRoute path="/users" authed={loggedIn} component={Users} userRole={user?.isAdmin} />
						<PrivateRoute path="/departments" authed={loggedIn} component={Departments} userRole={user?.isAdmin} />
						<PrivateRoute path="/emails" authed={loggedIn} component={Emails} userRole={user?.isAdmin} />

						<PrivateRoute path="/subscription/plan" authed={loggedIn} component={Plan} userRole={user?.isAdmin} />
						<PrivateRoute path="/subscription/payment" authed={loggedIn} component={Payment} userRole={user?.isAdmin} />
						<PrivateRoute path="/subscription/invoice" authed={loggedIn} component={Invoice} userRole={user?.isAdmin} />

						<PrivateRoute exact path="/embed-ticket-forms/trusted-domains" authed={loggedIn} component={EmbedTrustedDomains} userRole={user?.isAdmin} />
						<PrivateRoute exact path="/embed-ticket-forms/new" authed={loggedIn} component={EmbedTicketFormEditor} userRole={user?.isAdmin} />
						<PrivateRoute exact path="/embed-ticket-forms/:id(\d+)" authed={loggedIn} component={EmbedTicketFormEditor} userRole={user?.isAdmin} />
						<PrivateRoute exact path="/embed-ticket-forms" authed={loggedIn} component={EmbedTicketForms} userRole={user?.isAdmin} />

						<PrivateRoute path="/settings/type" authed={loggedIn} component={Types} userRole={user?.isAdmin} />
						<PrivateRoute path="/settings/priority" authed={loggedIn} component={Priority} userRole={user?.isAdmin} />
						<PrivateRoute path="/settings/tags" authed={loggedIn} component={Tags} userRole={user?.isAdmin} />
						<PrivateRoute path="/settings/quick-replies" authed={loggedIn} component={QuickReplies} userRole={user?.isAgent || user?.isAdmin} />
						<PrivateRoute path="/settings/transfering-codes" authed={loggedIn} component={TransferConnections} userRole={user?.isAdmin} />
						<Redirect from="/transfering-codes" to="/settings/transfering-codes" />
						<Redirect from="/settings/ticket-transfers" to="/settings/transfering-codes" />
						<PrivateRoute path="/settings/connect_cc" authed={loggedIn} component={Connection} userRole={user?.isAdmin} />
						<PrivateRoute path="/developer/:part?" authed={loggedIn} component={DeveloperOption} userRole={user?.isAdmin} />

						{/* we need a view for general users (role.customer) of each workspace to be able to interact with their tickets */}
						{/* a customer might be in multiple workspaces, so we need to be able to switch between them. */}
						{/* users should be able to create tickets, reply to them, and view a list of thickets */}
						{/* their view should be completely separate from the agent and admin view (different mainContainer component) */}

						<Route path='/404' component={NotFound} />
						<Route exact path="/500" component={ServerError} />
						<Redirect from='*' to='/404' />
					</Switch>
				</Suspense>
				</ChunkErrorBoundary>
			</div >
		</>
	);
}

export default withRouter(App);

if (document.getElementById('app')) {
	ReactDOM.render(
		<Provider store={store}>
			<BrowserRouter>
				<App />
			</BrowserRouter>
		</Provider>,
		document.getElementById('app')
	);
}
